import { queryOptions } from '@tanstack/react-query';
import { registerBaseURL, registerPartnersBaseURL } from '#constants/hosts';
import { client } from '#queries/utils';
import { common as partnerRegisterCommon } from '#proto/index.qonto.registration.partner_register';
import { ApiListOrganizationsResponse } from '#proto/qonto/registration/partner_register/organizations/firstparty/v1/list_organizations';
import {
  type RegistrationJsonApiResponse,
  // oxlint-disable-next-line sort-imports
  type RegisterPartnersRegistrationResult,
  ApiGetPartnerRegistrationResponse,
} from './types';
import { resolveRedirectionTokenOrganization } from './resolve-redirection-token-organization';

const ApiRegistration = partnerRegisterCommon.v1.ApiRegistration;
const ApiOrganization = partnerRegisterCommon.v1.ApiOrganization;
const ApiStakeholder = partnerRegisterCommon.v1.ApiStakeholder;

export const registerPartnersQueries = {
  all: ['register-partners'] as const,
  lists: () => [...registerPartnersQueries.all, 'list'] as const,
  list: () =>
    queryOptions({
      queryKey: [...registerPartnersQueries.lists()] as const,
      queryFn: async () => {
        const json = await client(`${registerPartnersBaseURL}/v1/organizations`);
        return ApiListOrganizationsResponse.fromJSON(json).organizations;
      },
    }),
  registration: (id?: string) =>
    queryOptions({
      queryKey: [...registerPartnersQueries.all, 'registration', id] as const,
      queryFn: async (): Promise<RegisterPartnersRegistrationResult> => {
        if (!id) {
          throw new Error('Registration ID is required');
        }

        const res = await client<RegistrationJsonApiResponse>(
          `${registerPartnersBaseURL}/client_api/registrations/${id}?include=organization,stakeholders`
        );

        const registration = ApiRegistration.fromJSON({
          ...res.data.attributes,
          id: res.data.id,
          organization_id: res.data.relationships.organization.data.id,
          stakeholder_ids: res.data.relationships.stakeholders.data.map(s => s.id),
        });

        const includedItems = res.included ?? [];

        const registrationStakeholders = includedItems
          .filter(item => item.type === 'stakeholders')
          .map(item =>
            ApiStakeholder.fromJSON({
              ...item.attributes,
              id: item.id,
              organization_id: res.data.relationships.organization.data.id,
              registration_id: res.data.id,
            })
          );

        const organizationId = res.data.relationships.organization.data.id;
        const organizationData = includedItems.find(
          item => item.type === 'organizations' && item.id === organizationId
        );

        if (!organizationData) {
          throw new Error(`Organization ${organizationId} not found in included resources`);
        }

        // Note: organization from ?include=organization does not populate `features`.
        // The Ember hook fetched features via a separate store.findAll('organization') call.
        // If features are needed, make a separate request to GET /client_api/organizations.
        const organization = ApiOrganization.fromJSON({
          ...organizationData.attributes,
          id: organizationData.id,
        });

        return {
          registration,
          stakeholders: registrationStakeholders,
          organization,
        };
      },
    }),
  partnerRegistration: (id: string) =>
    queryOptions({
      queryKey: [...registerPartnersQueries.all, 'partner-registration', id] as const,
      queryFn: async () => {
        const json = await client(`${registerBaseURL}/v1/partner_registrations/${id}`);

        return ApiGetPartnerRegistrationResponse.fromJSON(json);
      },
    }),
  redirectionTokenOrganization: (tokenId: string) =>
    queryOptions({
      queryKey: [
        ...registerPartnersQueries.all,
        'redirection-token-organization',
        tokenId,
      ] as const,
      staleTime: Infinity,
      retry: false,
      queryFn: () => resolveRedirectionTokenOrganization(tokenId),
    }),
};
